import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography, Tooltip, IconButton, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  AddOutlined, CloseOutlined, DragIndicatorOutlined,
  DriveFileRenameOutlineOutlined, CheckOutlined,
  ViewQuiltOutlined, GridViewOutlined,
} from '@mui/icons-material';
import { STEP_COLOR, SELECTION_COLOR } from './constants';

export default function StepStrip({
  steps, activeStepId,
  onSwitch, onAdd, onRename, onDelete, onReorder,
}) {
  const theme = useTheme();

  const [renamingId,    setRenamingId]    = useState(null);
  const [renameVal,     setRenameVal]     = useState('');
  const [dragId,        setDragId]        = useState(null);
  const [dropId,        setDropId]        = useState(null);
  const [hoveredId,     setHoveredId]     = useState(null);

  const inputRef = useRef(null);

  useEffect(() => {
    if (renamingId && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [renamingId]);

  const startRename = (step, e) => {
    e?.stopPropagation();
    setRenamingId(step._id);
    setRenameVal(step.title);
  };

  const commitRename = () => {
    if (renamingId && renameVal.trim()) onRename(renamingId, renameVal.trim());
    setRenamingId(null);
  };

  const handleDragStart = (e, id) => {
    setDragId(id);
    e.dataTransfer.effectAllowed = 'move';
    const ghost = document.createElement('div');
    ghost.style.cssText = 'position:fixed;top:-999px;width:32px;height:32px;border-radius:50%;background:#7c3aed;color:#fff;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:13px;font-family:inherit';
    ghost.textContent = steps.findIndex(s => s._id === id) + 1;
    document.body.appendChild(ghost);
    e.dataTransfer.setDragImage(ghost, 16, 16);
    setTimeout(() => document.body.removeChild(ghost), 0);
  };
  const handleDragOver = (e, id) => { e.preventDefault(); setDropId(id); };
  const handleDrop     = (e, id) => {
    e.preventDefault();
    if (dragId && dragId !== id) onReorder(dragId, id);
    setDragId(null); setDropId(null);
  };
  const handleDragEnd  = () => { setDragId(null); setDropId(null); };

  const activeIndex = steps.findIndex(s => s._id === activeStepId);

  return (
    <Box sx={{
      display: 'flex', alignItems: 'center',
      px: 3, py: '16px',
      background: 'transparent',
      overflowX: 'auto', flexShrink: 0,
      '&::-webkit-scrollbar': { height: 3 },
      '&::-webkit-scrollbar-thumb': { background: '#d4dbe6', borderRadius: 2 },
    }}>

      {steps.map((step, idx) => {
        const isActive    = step._id === activeStepId;
        const isCompleted = idx < activeIndex;
        const isDragging  = dragId === step._id;
        const isDragOver  = dropId === step._id && dragId !== step._id;
        const isHovered   = hoveredId === step._id;
        const isRenaming  = renamingId === step._id;
        const isSelection = step.stepType === 'selection';

        const primaryColor = theme.palette.primary.main;
        const circleActive = isActive || isCompleted;
        const circleBg     = circleActive ? primaryColor : theme.palette.background.paper;
        const circleBorder = circleActive ? 'none' : `2px solid ${theme.palette.divider}`;
        const circleColor  = circleActive ? '#fff' : theme.palette.text.disabled;
        const connectorBg  = idx < activeIndex ? primaryColor : theme.palette.divider;

        return (
          <React.Fragment key={step._id}>
            {/* ── Step pill ────────────────────────────────────────────── */}
            <Box
              draggable={!isRenaming}
              onDragStart={e => handleDragStart(e, step._id)}
              onDragOver={e => handleDragOver(e, step._id)}
              onDrop={e => handleDrop(e, step._id)}
              onDragEnd={handleDragEnd}
              onMouseEnter={() => setHoveredId(step._id)}
              onMouseLeave={() => setHoveredId(null)}
              onClick={() => { if (!isRenaming) onSwitch(step._id); }}
              sx={{
                position: 'relative',
                display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1,
                flexShrink: 0,
                cursor: isRenaming ? 'default' : 'pointer',
                opacity: isDragging ? 0.3 : 1,
                transition: 'opacity 0.15s',
                outline: isDragOver ? `2px solid ${alpha(primaryColor, 0.45)}` : '2px solid transparent',
                borderRadius: '20px',
                px: '6px', py: '4px',
                mx: '-4px',
              }}
            >
              {/* Drag handle — floats left of pill on hover */}
              {!isRenaming && (
                <Box
                  sx={{
                    position: 'absolute', left: -20,
                    display: 'flex', alignItems: 'center',
                    color: '#d4dbe6', cursor: 'grab',
                    opacity: isHovered ? 1 : 0,
                    transition: 'opacity 0.15s',
                    '&:active': { cursor: 'grabbing' },
                    '&:hover': { color: primaryColor },
                  }}
                  onMouseDown={e => e.stopPropagation()}
                >
                  <DragIndicatorOutlined sx={{ fontSize: 14 }} />
                </Box>
              )}

              {/* Circle with optional selection badge */}
              <Box sx={{ position: 'relative', flexShrink: 0 }}>
                <Box sx={{
                  width: 32, height: 32, borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700, fontSize: 13,
                  bgcolor: circleBg,
                  border: circleBorder,
                  color: circleColor,
                  transition: 'all 0.3s ease',
                  boxShadow: isActive ? `0 4px 16px ${primaryColor}44` : 'none',
                  userSelect: 'none',
                }}>
                  {idx + 1}
                </Box>
                {isSelection && (
                  <Box sx={{
                    position: 'absolute', bottom: -1, right: -1,
                    width: 10, height: 10, borderRadius: '50%',
                    background: SELECTION_COLOR,
                    border: '1.5px solid #fff',
                  }} />
                )}
              </Box>

              {/* Label or rename input */}
              {isRenaming ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box
                    component="input"
                    ref={inputRef}
                    value={renameVal}
                    onChange={e => setRenameVal(e.target.value)}
                    onBlur={commitRename}
                    onKeyDown={e => {
                      e.stopPropagation();
                      if (e.key === 'Enter') commitRename();
                      if (e.key === 'Escape') setRenamingId(null);
                    }}
                    onClick={e => e.stopPropagation()}
                    sx={{
                      border: `1.5px solid ${primaryColor}`,
                      borderRadius: '6px', outline: 'none',
                      background: '#fff',
                      fontSize: '0.72rem', fontWeight: 700,
                      color: primaryColor, fontFamily: 'inherit',
                      px: '6px', py: '3px',
                      width: Math.max(72, renameVal.length * 8),
                      transition: 'width 0.1s',
                    }}
                  />
                  <IconButton size="small"
                    onClick={e => { e.stopPropagation(); commitRename(); }}
                    sx={{ width: 22, height: 22, color: '#22c55e', background: alpha('#22c55e', 0.08), '&:hover': { background: alpha('#22c55e', 0.16) } }}>
                    <CheckOutlined sx={{ fontSize: 13 }} />
                  </IconButton>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: 11, letterSpacing: 0.5,
                      fontWeight: isActive ? 700 : 400,
                      color: isActive ? 'primary.main' : isCompleted ? 'text.secondary' : 'text.disabled',
                      textTransform: 'uppercase',
                      whiteSpace: 'nowrap',
                      transition: 'color 0.3s',
                      userSelect: 'none',
                    }}
                  >
                    {step.title}
                  </Typography>

                  {/* Rename + delete appear on hover */}
                  {isHovered && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, ml: 0.5 }}>
                      <Tooltip title="Rename" placement="top">
                        <IconButton size="small"
                          onClick={e => startRename(step, e)}
                          sx={{ width: 18, height: 18, color: '#9ea8b3', '&:hover': { color: primaryColor, background: alpha(primaryColor, 0.08) } }}>
                          <DriveFileRenameOutlineOutlined sx={{ fontSize: 11 }} />
                        </IconButton>
                      </Tooltip>
                      {steps.length > 1 && (
                        <Tooltip title="Delete" placement="top">
                          <IconButton size="small"
                            onClick={e => { e.stopPropagation(); onDelete(step._id); }}
                            sx={{ width: 18, height: 18, color: '#9ea8b3', '&:hover': { color: '#dc2626', background: alpha('#dc2626', 0.08) } }}>
                            <CloseOutlined sx={{ fontSize: 11 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  )}
                </Box>
              )}
            </Box>

            {/* ── Connector line ───────────────────────────────────────── */}
            {idx < steps.length - 1 && (
              <Box sx={{
                height: 2,
                flex: '1 1 auto', minWidth: 40, maxWidth: 100,
                mx: 1, flexShrink: 0,
                bgcolor: connectorBg,
                transition: 'background-color 0.3s ease',
              }} />
            )}
          </React.Fragment>
        );
      })}

      {/* ── Short connector into Add button ──────────────────────────── */}
      <Box sx={{ height: 2, width: 20, mx: 1, flexShrink: 0, bgcolor: theme.palette.divider }} />

      {/* ── Add step button — directly adds a fields step ────────────── */}
      <Tooltip title="Add step">
        <Box
          onClick={e => { e.stopPropagation(); onAdd(); }}
          sx={{
            flexShrink: 0,
            width: 32, height: 32, borderRadius: '50%',
            border: `2px dashed ${theme.palette.divider}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', color: theme.palette.text.disabled,
            transition: 'all 0.2s ease',
            '&:hover': {
              borderColor: theme.palette.primary.main,
              color: theme.palette.primary.main,
              background: alpha(theme.palette.primary.main, 0.06),
              boxShadow: `0 4px 12px ${theme.palette.primary.main}33`,
            },
          }}
        >
          <AddOutlined sx={{ fontSize: 16 }} />
        </Box>
      </Tooltip>
    </Box>
  );
}